import React, { useEffect, useState, useRef } from 'react';
import { Modal, message as antdMessage } from 'antd';
import { useAuth } from '~/views/redux/hooks/useAuth'
import { utils } from 'ethers'
import { providerOAS } from '~/blockchain/oas'
import { useTheme } from '~/common/theme/redux/hooks/useTheme'
import { useConnect, useAccount, useSignMessage, useDisconnect, useConnections } from 'wagmi';
import { useSDK } from '@metamask/sdk-react';

export default function SelectWallet(props) {
  const [isSigning, setIsSigning] = useState(false);
  const [shouldSign, setShouldSign] = useState(false); 
  const [networkAdded, setNetworkAdded] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const message = 'Login with Re.Monster';
  const { action, t } = props;
  const { actions } = useAuth();
  const { actions: actionTheme } = useTheme();
  const { connect, connectors } = useConnect();
  const { address, isConnected } = useAccount();
  const { signMessage } = useSignMessage();
  const { disconnect } = useDisconnect();

  const previousAddressRef = useRef();
  const { sdk } = useSDK();
  const connections = useConnections();

  useEffect(() => {
    if (isConnected && address) {
      if (previousAddressRef.current && previousAddressRef.current !== address) {
        disconnect(); 
        console.log("Account changed. Please reconnect with new account.")
      }
      previousAddressRef.current = address;
    }
  }, [address, isConnected, disconnect]);

  const getNetworkConfig = () => {
    const env = process.env.REACT_APP_NETWORK_ENV;
    
    const networkConfigs = {
      development: {
        chainId: '0x4EE5', 
        chainName: 'Sand Verse - Testnet',
        rpcUrls: ['https://rpc.sandverse.oasys.games'],
        blockExplorerUrls: ['https://scan.sandverse.oasys.games/'],
      },
      staging: {
        chainId: '0x736C', 
        chainName: 'MCH Verse Mainnet',
        rpcUrls: ['https://rpc.oasys.mycryptoheroes.net'],
        blockExplorerUrls: ['https://explorer.oasys.games/'],
      },
      production: {
        chainId: '0x736C', 
        chainName: 'MCH Verse Mainnet',
        rpcUrls: ['https://rpc.oasys.mycryptoheroes.net'],
        blockExplorerUrls: ['https://explorer.oasys.games/'],
      },
    };

    return networkConfigs[env] || networkConfigs.production;
  };

  const checkAndAddNetwork = async (provider) => {
    try {
      const networkConfig = getNetworkConfig();
      
      let currentChainId = await provider.request({
        method: 'eth_chainId',
        params: [],
      });
      const networkChainIdDecimal = parseInt(networkConfig.chainId, 16);

      if (currentChainId === networkChainIdDecimal) {
        return { success: true, networkAdded: false }; 
      }

      console.log('Current network:', currentChainId, 'Required:', networkChainIdDecimal);
      
      try {
        await provider.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: networkConfig.chainId }],
        });
        console.log('Switch network successful')
        return { success: true, networkAdded: false }; 
      } catch (switchError) {
        if (switchError.code === 4902 || switchError.code === -32603) {
          console.log('Network not found, adding network...');
          
          await provider.request({
            method: 'wallet_addEthereumChain',
            params: [
              {
                chainId: networkConfig.chainId,
                chainName: networkConfig.chainName,
                nativeCurrency: {
                  symbol: 'OAS',
                  decimals: 18,
                },
                rpcUrls: networkConfig.rpcUrls,
                blockExplorerUrls: networkConfig.blockExplorerUrls,
              },
            ],
          });
          
          console.log(`${networkConfig.chainName} added successfully!`);
          try {
            await provider.request({
              method: 'wallet_switchEthereumChain',
              params: [{ chainId: networkConfig.chainId }],
            });
            console.log('Switch network successful')
            return { success: true, networkAdded: true }; 
          } catch (postAddSwitchError) {
            console.error('Error switching network after adding:', postAddSwitchError);
            return { success: false, networkAdded: false };
          }
        } else {
          throw switchError;
        }
      }
    } catch (error) {
      console.error('Error checking/adding network:', error);
      throw error;
    }
  };

  useEffect(() => {
    if (isConnected && address && shouldSign && !isSigning && connections.length > 0) {
      setIsSigning(true);
      signMessage(
        { message },
        {
          onSuccess: (signature) => {
            let dataUser = {
              address_wallet: address,
              signature: signature,
              message: message,
              wallet: 'MW',
              is_market: true,
              client: 'MARKETPLACE',
            };
            localStorage.setItem('wallet', 'MW');
            actions.login(dataUser, (res) => {
              actionTheme.updateInfoUser(res);
              setIsSigning(false);
              setShouldSign(false); 
            });
          },
          onError: (error) => {
            console.error('Signing error:', error);
            antdMessage.error('Signing error.');
            setIsSigning(false);
            setShouldSign(false); 
            disconnect();
          },
        }
      );
    }
  }, [isConnected, address, shouldSign, isSigning, signMessage, actions, actionTheme, disconnect]);

  const handleConnectMetamask = async () => {
    if (isConnecting) return;
    setIsConnecting(true);
    const metaMaskConnector = connectors.find((c) => c.name === 'MetaMask');
    if (metaMaskConnector) {
      try {
        await disconnect();
        if (sdk) {
          await sdk.terminate();
        }
        await new Promise((resolve) => setTimeout(resolve, 500));

        const connectionResult = await sdk.connect();
        if (!connectionResult) {
          throw new Error('Failed to connect to MetaMask SDK');
        }
        const provider = sdk.getProvider();
        if (!provider) {
          throw new Error('Provider is invalid after connection');
        }
        await connect({ 
          connector: metaMaskConnector,
          chainId: await sdk.getProvider().request({ method: 'eth_chainId' }).then(id => parseInt(id, 16)), 
        });
        setShouldSign(true);
        action.handleCancel();
      } catch (error) {
        console.error('Connection error:', error);
        antdMessage.error('Connection error.');
      }
    } else {
      console.error('MetaMask connector not found');
      antdMessage.error('MetaMask connector not found.');
    }
  };

  const handleConnectOAS = async () => {
    try {
      action.handleCancel();

      if (providerOAS.disconnect) {
        await providerOAS.disconnect();
      }
      localStorage.removeItem("walletconnect"); 

      await providerOAS.connect();

      let retries = 0;
      while ((!providerOAS.accounts || providerOAS.accounts.length === 0) && retries < 10) {
        await new Promise((resolve) => setTimeout(resolve, 500)); 
        retries++;
      }

      const address = providerOAS.accounts?.[0];
      if (!address) {
        antdMessage.error('Failed to retrieve wallet address from OAS provider.');
        return;
      }

      try {
        const networkResult = await checkAndAddNetwork(providerOAS);
        
        if (!networkResult.success) {
          console.error('Network setup failed');
          return;
        }

        if (networkResult.networkAdded) {
          console.log('Network was added, showing QR code again...');
          setNetworkAdded(true);
          
          if (providerOAS.disconnect) {
            await providerOAS.disconnect();
          }
          localStorage.removeItem("walletconnect");
          
          antdMessage.success('Network added successfully! Please scan QR code again to reconnect.');
          
          setTimeout(() => {
            handleConnectOAS();
          }, 1000);
          
          return;
        }

      } catch (networkError) {
        console.error('Network error:', networkError);
        return;
      }

      await new Promise((resolve) => setTimeout(resolve, 500));

      const signature = await providerOAS.request({
        method: 'personal_sign',
        params: [utils.hexlify(utils.toUtf8Bytes(message)), address],
      });

      const dataUser = {
        address_wallet: address,
        signature,
        message,
        wallet: 'OAS',
        is_market: true,
        client: 'MARKETPLACE',
      };

      localStorage.setItem('wallet', 'OAS');

      actions.login(dataUser, (res) => {
        actionTheme.updateInfoUser(res);
        if (res?.player_language) {
          actionTheme.onLocaleChange(res?.player_language);
        }
      });
    } catch (error) {
      console.error('Error connecting with OAS Wallet:', error);
      antdMessage.error('Failed to connect with OAS Wallet.');
    }
  };

  return (
    <>
      <Modal
        width={700}
        centered
        title={
          <h3 className="flex justify-center text-[20px] font-bold">
            {t('modal.connect.title')}
          </h3>
        }
        open={action?.isModalOpen}
        onCancel={action?.handleCancel}
        footer={[]}
      >
        <div className="flex justify-center gap-5 pt-10 flex flex-wrap gap-2 w-full mb-2">
          <div
            className="st-wallet rounded-[10px]"
            onClick={handleConnectMetamask}
          >
            <div className="w-[60px] h-[60px] bg-white flex justify-center items-center rounded-[8px]">
              <img
                className="w-[50px] h-[50px] object-contain"
                src={`${process.env.PUBLIC_URL}/imgs/logo/metamask.png`}
                alt=""
              />
            </div>
            {t('modal.connect.metamask')}
          </div>
          <div
            className="st-wallet-1 rounded-[10px]"
            onClick={handleConnectOAS}
          >
            <div className="w-[60px] h-[60px] flex justify-center items-center rounded-[8px]">
              <img
                className="w-[100%] h-[100%] object-contain rounded-[8px]"
                src={`${process.env.PUBLIC_URL}/imgs/logo/oas-wallet.png`}
                alt=""
              />
            </div>
            <div>
              <div>{t('modal.connect.oas')}</div>
              <div className="flex items-center gap-1">
                <img
                  className="h-[16px] object-contain"
                  src={`${process.env.PUBLIC_URL}/imgs/logo/wallet-connect.svg`}
                  alt=""
                />
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </>
  )
}
